/**
 * Published screener presets — the wire, and the one place the two spellings meet.
 *
 * The store calls them `base_filter` and `sorts`; the contract calls them
 * `filter` and `sort`. `screener-grid` speaks contract, because it is a
 * WebView-hostable island that should not know what a server calls its columns
 * — so the mapping lives here, in exactly one direction each way.
 *
 * Follows `api/me.ts`'s rules: the base URL is read once, a missing one makes
 * the feature *degrade* rather than error, and a failed read is an empty list.
 * **Publishing throws**, because somebody who clicked Publish and saw nothing
 * would assume it worked — the same split `api/admin.ts` makes.
 */

import type { ScreenerConfig } from "@qufolio/model";
import type { PresetDefinition, PresetSummary } from "@qufolio/screener-grid";

import { EMPTY_OVERLAY, type Overlay } from "../lib/screen-overlay";
import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL);

/** Whether there is a server to ask. Off, the Presets tab is not offered. */
export const presetsConfigured: boolean = BASE !== "";

/** The wire shape. `sorts`/`baseFilter` are the store's names, not the contract's. */
interface WirePreset {
  key: string;
  name: string;
  description: string | null;
  ownerKind: "system" | "org" | "user";
  ownerKey: string | null;
  ownerName?: string;
  version?: number;
  /** The group's conditions are withheld and evaluated on the server. */
  sealedConditions?: boolean;
  market: string;
  groupCodes: string[];
  columns?: ScreenerConfig["columns"];
  baseFilter?: ScreenerConfig["filter"];
  sorts?: ScreenerConfig["sort"];
  /** Null for a screen stored before conditions travelled with one. */
  savedFilters?: ScreenerConfig["savedFilters"] | null;
  /** Null means alphabetical — every screen stored before the field existed. */
  savedFilterCategoryOrder?: ScreenerConfig["savedFilterCategoryOrder"] | null;
  /** A JP trade-list screen; null is ordinary (`docs/plans/jp-recs.md`). */
  recs?: ScreenerConfig["recs"] | null;
  /** A TopDown screen; null is ordinary (`docs/features/topdown.md`). */
  topDown?: ScreenerConfig["topDown"] | null;
  /** A Seasonality screen; null is ordinary (`docs/features/seasonality.md`). */
  seasonality?: ScreenerConfig["seasonality"] | null;
  /** The bar's ▲▼ key; null is All (`docs/plans/movers.md`). */
  direction?: ScreenerConfig["direction"] | null;
  /** On a screen of the group being authored: an unpublished draft is held. */
  hasDraft?: boolean;
}

/**
 * A listing row as this app reads it: the island's summary plus what only the
 * host acts on.
 *
 * `hasDraft` is here rather than on `PresetSummary` because nothing in
 * `@qufolio/screener-grid` draws it — the tab strip is the host's. It is passed
 * through `toSummary` by name, which is the whole point of writing it down:
 * this mapper whitelists, and a server field it does not name vanishes without
 * an error (`version` on layouts, 09-29 → 10-04).
 */
export type ScreenSummary = PresetSummary & {
  hasDraft?: boolean;
  /** A JP trade-list screen — listed under Special (`docs/plans/jp-recs.md`). */
  recs?: ScreenerConfig["recs"];
  /** A TopDown screen — listed under Special (`docs/features/topdown.md`). */
  topDown?: ScreenerConfig["topDown"];
  /** A Seasonality screen — listed under Special (`docs/features/seasonality.md`). */
  seasonality?: ScreenerConfig["seasonality"];
};

function toSummary(wire: WirePreset): ScreenSummary {
  return {
    key: wire.key,
    name: wire.name,
    description: wire.description ?? null,
    ownerKind: wire.ownerKind,
    ownerKey: wire.ownerKey ?? null,
    ...(typeof wire.ownerName === "string" ? { ownerName: wire.ownerName } : {}),
    ...(typeof wire.version === "number" ? { version: wire.version } : {}),
    // By name, for `hasDraft`'s reason. Only `true` is carried: absent and
    // false both mean open, and one spelling of "open" is one fewer to compare.
    ...(wire.sealedConditions === true ? { sealedConditions: true } : {}),
    market: wire.market,
    groupCodes: Array.isArray(wire.groupCodes) ? wire.groupCodes : [],
    ...(typeof wire.hasDraft === "boolean" ? { hasDraft: wire.hasDraft } : {}),
    // By name, `hasDraft`'s reason: unnamed, the screen list has no Special.
    ...(wire.recs && typeof wire.recs === "object" ? { recs: wire.recs } : {}),
    // By name, for the same reason: unnamed, a TopDown opens as an empty screen.
    ...(wire.topDown && typeof wire.topDown === "object" ? { topDown: wire.topDown } : {}),
    ...(wire.seasonality && typeof wire.seasonality === "object"
      ? { seasonality: wire.seasonality }
      : {}),
  };
}

export async function fetchPresets(): Promise<ScreenSummary[]> {
  if (!BASE) return [];
  try {
    const response = await fetch(`${BASE}/presets`, { headers: await requestHeaders() });
    if (!response.ok) return [];
    const body = (await response.json()) as WirePreset[];
    return Array.isArray(body) ? body.map(toSummary) : [];
  } catch {
    return [];
  }
}

export async function fetchPreset(key: string): Promise<PresetDefinition | null> {
  if (!BASE) return null;
  try {
    const response = await fetch(`${BASE}/presets/${encodeURIComponent(key)}`, {
      headers: await requestHeaders(),
    });
    // 404 covers both "no such preset" and "not yours" — the server does not
    // distinguish them on purpose, so that a key cannot be probed for.
    if (!response.ok) return null;
    const wire = (await response.json()) as WirePreset;
    return {
      ...toSummary(wire),
      columns: wire.columns ?? [],
      filter: wire.baseFilter,
      sort: wire.sorts,
      // Passed through as it arrived — `?? []` here is the bug this field is
      // shaped to avoid, since it would turn "this screen predates the column"
      // into "this screen has no conditions".
      savedFilters: wire.savedFilters ?? null,
      savedFilterCategoryOrder: wire.savedFilterCategoryOrder ?? null,
      // By name, or the mapper drops it and a Recs screen opens as an ordinary
      // one (the whitelist's lesson, `version` on layouts).
      recs: wire.recs ?? null,
      topDown: wire.topDown ?? null,
      seasonality: wire.seasonality ?? null,
      // By name, or a screen saved on ▼ reopens on All.
      ...(isDirection(wire.direction) ? { direction: wire.direction } : {}),
    };
  } catch {
    return null;
  }
}

function isDirection(value: unknown): value is NonNullable<ScreenerConfig["direction"]> {
  return value === "up" || value === "down";
}

/**
 * Publish, or replace, one preset. Admin only — the server refuses otherwise.
 *
 * The key is derived from the name rather than asked for. A club admin naming a
 * screen "Momentum" should not also have to invent a slug, and the collision
 * they would otherwise cause is exactly what `uq_preset_owner_name` refuses —
 * so deriving it makes the two agree instead of failing later.
 */
export async function publishPreset(input: {
  name: string;
  description: string;
  ownerKey: string;
  market?: string;
  groupCodes?: string[];
  columns: ScreenerConfig["columns"];
  filter: ScreenerConfig["filter"];
  sort: ScreenerConfig["sort"];
  recs?: ScreenerConfig["recs"];
  topDown?: ScreenerConfig["topDown"];
  seasonality?: ScreenerConfig["seasonality"];
  direction?: ScreenerConfig["direction"];
}): Promise<void> {
  if (!BASE) throw new Error("no server configured");
  const key = slug(`${input.ownerKey}-${input.name}`);
  const response = await fetch(`${BASE}/author/presets/${encodeURIComponent(key)}`, {
    method: "PUT",
    headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({
      name: input.name,
      description: input.description,
      ownerKind: "org",
      ownerKey: input.ownerKey,
      market: input.market ?? "US",
      groupCodes: input.groupCodes ?? [],
      columns: input.columns ?? [],
      baseFilter: input.filter ?? null,
      sorts: input.sort ?? [],
      // A Recs or TopDown screen stays one (`docs/features/topdown.md`); omitted
      // leaves a stored one alone on the server, as the user's own screens do.
      ...(input.recs ? { recs: input.recs } : {}),
      ...(input.topDown ? { topDown: withoutTicks(input.topDown) } : {}),
      ...(input.seasonality ? { seasonality: input.seasonality } : {}),
      ...(input.direction ? { direction: input.direction } : {}),
    }),
  });
  if (!response.ok) throw new Error((await response.text()) || "publish failed");
}

/**
 * Republish a group screen that already exists — **under its own key**.
 *
 * `publishPreset` derives the key from `ownerKey-name`, which is right for a
 * screen being created and wrong for every one that exists: *Persons* is
 * `jp-persons`, and the slug writes `grp-jp-persons`, a second screen
 * (`docs/plans/author-screen-drafts.md`). The key is the caller's, read from
 * the listing, never rebuilt from a name.
 */
export async function republishPreset(input: {
  key: string;
  name: string;
  description: string;
  ownerKey: string;
  market?: string;
  groupCodes?: string[];
  columns: ScreenerConfig["columns"];
  filter: ScreenerConfig["filter"];
  sort: ScreenerConfig["sort"];
  recs?: ScreenerConfig["recs"];
  topDown?: ScreenerConfig["topDown"];
  seasonality?: ScreenerConfig["seasonality"];
  direction?: ScreenerConfig["direction"];
}): Promise<void> {
  if (!BASE) throw new Error("no server configured");
  const response = await fetch(`${BASE}/author/presets/${encodeURIComponent(input.key)}`, {
    method: "PUT",
    headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({
      name: input.name,
      description: input.description,
      ownerKind: "org",
      ownerKey: input.ownerKey,
      market: input.market ?? "US",
      groupCodes: input.groupCodes ?? [],
      columns: input.columns ?? [],
      baseFilter: input.filter ?? null,
      sorts: input.sort ?? [],
      // A Recs or TopDown screen stays one (`docs/features/topdown.md`); omitted
      // leaves a stored one alone on the server, as the user's own screens do.
      ...(input.recs ? { recs: input.recs } : {}),
      ...(input.topDown ? { topDown: withoutTicks(input.topDown) } : {}),
      ...(input.seasonality ? { seasonality: input.seasonality } : {}),
      ...(input.direction ? { direction: input.direction } : {}),
    }),
  });
  if (!response.ok) throw new Error((await response.text()) || "publish failed");
}

// ── an author's draft of a group screen ─────────────────────────────────────
//
// `docs/plans/author-screen-drafts.md`. While authoring as a group, that group's
// own screens are edited as **one shared draft per screen** (owner, 2026-10-06,
// decision 1), held beside the screen and invisible to members until Publish.
// The draft is the whole definition — formats, widths, order, hiding — because
// what is published is exactly what was on screen (decision 3).

/** The draft as this app reads it: contract names, like `PresetDefinition`. */
export interface ScreenDraft {
  key: string;
  columns: NonNullable<ScreenerConfig["columns"]>;
  filter: ScreenerConfig["filter"];
  sort: NonNullable<ScreenerConfig["sort"]>;
  savedFilters: ScreenerConfig["savedFilters"] | null;
  savedFilterCategoryOrder: ScreenerConfig["savedFilterCategoryOrder"] | null;
  /** A Recs / TopDown screen's own block; null is an ordinary screen. */
  recs: ScreenerConfig["recs"] | null;
  topDown: ScreenerConfig["topDown"] | null;
  seasonality: ScreenerConfig["seasonality"] | null;
  /** The ▲▼ key; absent is All. */
  direction?: ScreenerConfig["direction"];
  /** The person who last wrote it — the actor, never the group account. */
  updatedBy: string;
  updatedByName: string | null;
  updatedAt: string;
}

/** What a draft write carries — `lib/screen-sync`'s `Definition`, by shape. */
export interface DraftDefinition {
  columns: NonNullable<ScreenerConfig["columns"]>;
  filter: ScreenerConfig["filter"];
  sort: NonNullable<ScreenerConfig["sort"]>;
  savedFilters: NonNullable<ScreenerConfig["savedFilters"]>;
  savedFilterCategoryOrder: NonNullable<ScreenerConfig["savedFilterCategoryOrder"]>;
  /** Omitted leaves the draft's alone — the panel's sync may not know the field. */
  recs?: ScreenerConfig["recs"];
  topDown?: ScreenerConfig["topDown"];
  seasonality?: ScreenerConfig["seasonality"];
  /** Omitted is All — part of every definition, as on `saveScreen`. */
  direction?: ScreenerConfig["direction"];
}

interface WireDraft {
  key: string;
  columns?: ScreenerConfig["columns"];
  baseFilter?: ScreenerConfig["filter"] | null;
  sorts?: ScreenerConfig["sort"];
  savedFilters?: ScreenerConfig["savedFilters"] | null;
  savedFilterCategoryOrder?: ScreenerConfig["savedFilterCategoryOrder"] | null;
  recs?: ScreenerConfig["recs"] | null;
  topDown?: ScreenerConfig["topDown"] | null;
  seasonality?: ScreenerConfig["seasonality"] | null;
  direction?: ScreenerConfig["direction"] | null;
  updatedBy?: string;
  updatedByName?: string | null;
  updatedAt?: string;
}

function toDraft(wire: WireDraft): ScreenDraft {
  return {
    key: wire.key,
    columns: wire.columns ?? [],
    filter: wire.baseFilter ?? undefined,
    sort: wire.sorts ?? [],
    // As it arrived, for `fetchPreset`'s reason: null is "no library", not "empty".
    savedFilters: wire.savedFilters ?? null,
    savedFilterCategoryOrder: wire.savedFilterCategoryOrder ?? null,
    // By name, for `fetchPreset`'s reason: dropped here, a draft of a Recs or
    // TopDown screen reopened as an ordinary one.
    recs: wire.recs && typeof wire.recs === "object" ? wire.recs : null,
    topDown: wire.topDown && typeof wire.topDown === "object" ? wire.topDown : null,
    seasonality:
      wire.seasonality && typeof wire.seasonality === "object" ? wire.seasonality : null,
    ...(isDirection(wire.direction) ? { direction: wire.direction } : {}),
    updatedBy: String(wire.updatedBy ?? ""),
    updatedByName: wire.updatedByName ?? null,
    updatedAt: asUtc(String(wire.updatedAt ?? "")),
  };
}

/**
 * The server stamps `updatedAt` in UTC **without a zone**, and `Date.parse`
 * reads a zoneless ISO date-time as *local* — "edited 2 min ago" would read
 * "7 h ago" in Pacific. Marked UTC here, once, so no reader has to know.
 */
function asUtc(iso: string): string {
  if (!/^\d{4}-\d\d-\d\d[ T]\d/.test(iso) || /([zZ]|[+-]\d\d:?\d\d)$/.test(iso)) return iso;
  return `${iso.replace(" ", "T")}Z`;
}

const DRAFT = (key: string) => `${BASE}/author/presets/${encodeURIComponent(key)}/draft`;

/**
 * The draft of one screen, or `null` when there is none — **404 is the answer
 * "no draft"**, not a failure.
 *
 * **Any other failure throws**, unlike the reads above. A draft read that
 * degraded to "none" would fill the panel from the published screen, and the
 * next edit would then overwrite the real draft with published-plus-one-change:
 * somebody's unpublished work, gone because a request timed out.
 */
export async function fetchDraft(key: string): Promise<ScreenDraft | null> {
  if (!BASE) return null;
  const response = await fetch(DRAFT(key), { headers: await requestHeaders() });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error((await response.text()) || "could not read the draft");
  return toDraft((await response.json()) as WireDraft);
}

/** Write the draft, whole — the store's names on the wire. */
export async function saveDraft(key: string, definition: DraftDefinition): Promise<ScreenDraft> {
  const wire = await write<WireDraft>(DRAFT(key), {
    method: "PUT",
    body: JSON.stringify({
      columns: definition.columns ?? [],
      baseFilter: definition.filter ?? null,
      sorts: definition.sort ?? [],
      savedFilters: definition.savedFilters ?? [],
      savedFilterCategoryOrder: definition.savedFilterCategoryOrder ?? [],
      ...(definition.recs ? { recs: definition.recs } : {}),
      ...(definition.topDown ? { topDown: withoutTicks(definition.topDown) } : {}),
      ...(definition.seasonality ? { seasonality: definition.seasonality } : {}),
      ...(definition.direction ? { direction: definition.direction } : {}),
    }),
  });
  return toDraft(wire);
}

/** Throw the draft away. The published screen is untouched. */
export async function discardDraft(key: string): Promise<void> {
  await write<{ removed: unknown }>(DRAFT(key), { method: "DELETE" });
}

/**
 * Publish the draft to the screen's **own key** and delete it, server-side, in
 * one step. Returns the screen as now published — `version` is what moves.
 */
export async function publishDraft(key: string): Promise<ScreenSummary> {
  const wire = await write<WirePreset>(`${DRAFT(key)}/publish`, { method: "POST" });
  return toSummary(wire);
}

/** Lowercase, hyphenated, ASCII. Stable for the same name, which is the point. */
export function slug(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "preset"
  );
}

// ── A user's own screens ────────────────────────────────────────────────────
//
// The same rows, written through a different router. `/presets` is the read
// side for all three owner kinds; `/screens` writes only your own, and does not
// read `ownerKind` from the body at all — so there is no request here that could
// publish to everybody or write into a club's shelf, however this file is
// edited later.
//
// **These throw**, unlike the reads above, and it is `api/journal.ts`'s split:
// a listing that comes back empty degrades to a screen with no tabs but your
// own, where a create that failed silently leaves somebody typing into a tab
// that does not exist.

const SCREEN_BASE = `${BASE}/screens`;

async function write<T>(url: string, init: RequestInit): Promise<T> {
  if (!BASE) throw new Error("no server configured");
  const response = await fetch(url, {
    ...init,
    headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error((await response.text()) || `${init.method} failed`);
  return (await response.json()) as T;
}

export interface NewScreen {
  name: string;
  market?: string;
  groupCodes?: string[];
  columns?: ScreenerConfig["columns"];
  filter?: ScreenerConfig["filter"];
  sort?: ScreenerConfig["sort"];
  /**
   * The named conditions. **Omitted means "I am not saying", not "none"** — the
   * route leaves a stored library alone rather than clearing it, which is the
   * one exception to the replace-don't-merge rule `saveScreen` states below.
   * Without the exception `renameScreen`, which sends back what it read, would
   * delete the library of any screen still falling back to its panel's.
   */
  savedFilters?: ScreenerConfig["savedFilters"];
  /** The library's category order — omitted leaves it alone, as `savedFilters`. */
  savedFilterCategoryOrder?: ScreenerConfig["savedFilterCategoryOrder"];
  /**
   * A TopDown screen (`docs/features/topdown.md`). Omitted leaves a stored one
   * alone — the panel's own sync does not know the field, and must not turn a
   * TopDown into an ordinary screen. Never carries ticks: those are the panel's.
   */
  topDown?: ScreenerConfig["topDown"];
  /** A Seasonality screen (`docs/features/seasonality.md`); omitted leaves a stored one alone. */
  seasonality?: ScreenerConfig["seasonality"];
  /**
   * The bar's ▲▼ key. Unlike the two above, **omitted is All** and clears a
   * stored one: it is part of every screen's definition, so the route's
   * replace rule is the right one.
   */
  direction?: ScreenerConfig["direction"];
}

/** A TopDown as a screen holds it: what a reader ticked stays on their panel. */
function withoutTicks(td: NonNullable<ScreenerConfig["topDown"]>): NonNullable<ScreenerConfig["topDown"]> {
  const { ticks: _ticks, ...rest } = td;
  return rest;
}

/** Create one of the caller's screens. The server mints the key and returns it. */
export async function createScreen(input: NewScreen): Promise<PresetSummary> {
  const wire = await write<WirePreset>(SCREEN_BASE, {
    method: "POST",
    body: JSON.stringify({
      name: input.name,
      market: input.market ?? "US",
      groupCodes: input.groupCodes ?? [],
      columns: input.columns ?? [],
      baseFilter: input.filter ?? null,
      sorts: input.sort ?? [],
      ...(input.savedFilters ? { savedFilters: input.savedFilters } : {}),
      ...(input.savedFilterCategoryOrder
        ? { savedFilterCategoryOrder: input.savedFilterCategoryOrder }
        : {}),
      ...(input.topDown ? { topDown: withoutTicks(input.topDown) } : {}),
      ...(input.seasonality ? { seasonality: input.seasonality } : {}),
      ...(input.direction ? { direction: input.direction } : {}),
    }),
  });
  return toSummary(wire);
}

/**
 * Replace one of the caller's screens, whole.
 *
 * **Whole, because the route replaces rather than merges** — a screen is one
 * definition, and a partial body produces one nobody authored. The practical
 * consequence for every caller: a field you do not send is not left alone, it is
 * *cleared*. `groupCodes` is the one that bites, since the panel does not edit
 * the universe and would otherwise blank it on the first column change.
 *
 * **`savedFilters` is the exception, and it is one on the server**, so it holds
 * however this file is edited: an omitted key leaves the stored library alone.
 * The panel always sends it, so the exception is for callers that read a screen
 * and write it back — `renameScreen` below — which would otherwise clear the
 * library of every screen still falling back to its panel's.
 */
export async function saveScreen(
  key: string,
  input: NewScreen & { name: string },
): Promise<PresetSummary> {
  const wire = await write<WirePreset>(`${SCREEN_BASE}/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify({
      name: input.name,
      market: input.market ?? "US",
      groupCodes: input.groupCodes ?? [],
      columns: input.columns ?? [],
      baseFilter: input.filter ?? null,
      sorts: input.sort ?? [],
      ...(input.savedFilters ? { savedFilters: input.savedFilters } : {}),
      ...(input.savedFilterCategoryOrder
        ? { savedFilterCategoryOrder: input.savedFilterCategoryOrder }
        : {}),
      ...(input.topDown ? { topDown: withoutTicks(input.topDown) } : {}),
      ...(input.seasonality ? { seasonality: input.seasonality } : {}),
      ...(input.direction ? { direction: input.direction } : {}),
    }),
  });
  return toSummary(wire);
}

/**
 * Rename one, by sending its definition back with a new name.
 *
 * **A read then a write**, for `saveScreen`'s reason: there is no route that
 * takes a name on its own, and adding one would be a second write path to keep
 * in step for the sake of a round trip on a gesture that happens occasionally.
 *
 * The read-modify-write is a race only against yourself on another device, and
 * losing it costs a rename that has to be repeated.
 */
export async function renameScreen(key: string, name: string): Promise<PresetSummary> {
  const current = await fetchPreset(key);
  if (!current) throw new Error("that screen is gone");
  return saveScreen(key, {
    name,
    market: current.market,
    groupCodes: current.groupCodes,
    columns: current.columns ?? [],
    filter: current.filter,
    sort: current.sort,
    // `?? undefined` and never `?? []`: a screen still falling back to its
    // panel's library reads back null here, and sending `[]` would make a
    // rename delete the conditions.
    savedFilters: current.savedFilters ?? undefined,
    savedFilterCategoryOrder: current.savedFilterCategoryOrder ?? undefined,
    // Omitted is All, so a rename that dropped it would reset the key.
    direction: current.direction,
  });
}

/** Delete one of the caller's screens. A club's answers 404, which throws here. */
export async function deleteScreen(key: string): Promise<void> {
  await write<{ deleted: number }>(`${SCREEN_BASE}/${encodeURIComponent(key)}`, {
    method: "DELETE",
  });
}

// ── a member's own layer over a screen ──────────────────────────────────────
//
// Addressed by a screen the caller can **read**, not one they own — the whole
// point of a layer is that it sits over somebody else's screen. The body carries
// no user, so there is no request here that writes anybody else's view.

/** The caller's layer over one screen. An empty one is the ordinary answer. */
export async function fetchOverlay(key: string): Promise<Overlay> {
  if (!BASE) return EMPTY_OVERLAY;
  try {
    const response = await fetch(`${SCREEN_BASE}/${encodeURIComponent(key)}/overlay`, {
      headers: await requestHeaders(),
    });
    // A read that fails degrades to "no layer", which draws the club's screen as
    // published — wrong in the member's favour, and visibly so, where a throw
    // would leave the panel with nothing at all.
    if (!response.ok) return EMPTY_OVERLAY;
    const wire = (await response.json()) as Partial<Overlay>;
    return {
      columns: wire.columns ?? [],
      filter: wire.filter,
      columnView: wire.columnView ?? {},
      sort: wire.sort ?? [],
      // The conditions the member linked in (`docs/plans/screen-links.md`).
      // Named here, or this whitelist drops them without a word.
      savedFilters: Array.isArray(wire.savedFilters) ? wire.savedFilters : [],
    };
  } catch {
    return EMPTY_OVERLAY;
  }
}

/** Replace the caller's layer. Throws — a lost layer is a lost arrangement. */
export async function saveOverlay(key: string, overlay: Overlay): Promise<void> {
  await write<unknown>(`${SCREEN_BASE}/${encodeURIComponent(key)}/overlay`, {
    method: "PUT",
    body: JSON.stringify({
      columns: overlay.columns ?? [],
      filter: overlay.filter ?? null,
      columnView: overlay.columnView ?? {},
      sort: overlay.sort ?? [],
      savedFilters: overlay.savedFilters ?? [],
    }),
  });
}
